import * as onboardingApi from '../services/onboardingApi';

/**
 * Determines the correct onboarding entry point for a user based on their current state
 * Uses /me endpoint with JWT authentication - no user ID needed
 * @returns The route path where the user should be redirected
 */
export async function getOnboardingEntryPoint(): Promise<string> {
    try {
        console.log('[OnboardingRouter] Checking onboarding status using /me endpoint');

        // Fetch current onboarding status using /me endpoint (JWT auth)
        const statusResponse = await onboardingApi.getCurrentOnboardingStatus();
        const status: onboardingApi.OnboardingStatus = (statusResponse.payload || statusResponse) as unknown as onboardingApi.OnboardingStatus;

        console.log('[OnboardingRouter] Onboarding status:', {
            resumeUploaded: status.resumeUploaded,
            resumeParsed: status.resumeParsed,
            hasPreferences: status.hasPreferences,
            hasMissingQuestions: status.hasMissingQuestions,
            pendingQuestionsCount: status.pendingQuestionsCount,
            profileCompleted: status.profileCompleted
        });

        // If profile is already completed, go to dashboard
        if (status.profileCompleted) {
            console.log('[OnboardingRouter] Profile already completed → dashboard');
            return '/candidate/dashboard';
        }

        // Decision tree based on onboarding state
        // Check resumeParsed first - candidates may have uploaded resume during application (/auth/apply)
        // In that case, resumeUploaded may be false but resumeParsed is true
        if (!status.resumeParsed) {
            // Resume not parsed yet → need to upload/wait for parsing
            console.log('[OnboardingRouter] Resume not parsed → resume-upload');
            return '/welcome/resume-upload';
        }

        // Resume is parsed - follow sequential flow: Review → Goals → Questions → Complete

        // Step 1: ALWAYS show review page first (unless preferences already set)
        // Preferences being set indicates user has completed review and goals
        if (!status.hasPreferences) {
            // User hasn't set preferences yet, so they haven't completed the sequential flow
            // Always start with review page
            console.log('[OnboardingRouter] Resume parsed but no preferences → profile-review (start sequential flow)');
            return '/welcome/profile-review';
        }

        // Step 2: Preferences are set, check for missing questions
        if (status.hasMissingQuestions && status.pendingQuestionsCount > 0) {
            // Has unanswered questions → go to onboarding chat
            console.log('[OnboardingRouter] Preferences set, has pending questions → onboarding-chat');
            return '/welcome/onboarding-chat';
        }

        // Step 3: Everything is done but profile not marked complete
        // This shouldn't normally happen, but handle gracefully
        // Could happen if user completed all steps but markProfileCompleted wasn't called
        console.log('[OnboardingRouter] All steps complete but profile not marked → should mark complete');
        return '/welcome/onboarding-chat'; // Send to chat which will detect all done and mark complete

    } catch (error) {
        console.error('[OnboardingRouter] Failed to fetch onboarding status:', error);

        // On error, check if it's a 404 (candidate doesn't exist)
        if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
            // Candidate doesn't exist yet (new user) → start with intro
            console.log('[OnboardingRouter] Candidate not found (404) → intro');
            return '/welcome/intro';
        }

        // For 401/500/other errors, redirect to dashboard
        // The user is authenticated but backend had an issue - dashboard will handle gracefully
        console.log('[OnboardingRouter] API error occurred → fallback to dashboard');
        return '/candidate/dashboard';
    }
}

/**
 * Checks if a user should skip the intro page
 * Uses /me endpoint with JWT authentication
 * @returns true if user should skip intro, false otherwise
 */
export async function shouldSkipIntro(): Promise<boolean> {
    try {
        const statusResponse = await onboardingApi.getCurrentOnboardingStatus();
        const status: onboardingApi.OnboardingStatus = (statusResponse.payload || statusResponse) as unknown as onboardingApi.OnboardingStatus;

        // Skip intro if resume is already uploaded or parsed
        return status.resumeUploaded || status.resumeParsed;
    } catch (error) {
        // If candidate doesn't exist, don't skip intro
        return false;
    }
}

/**
 * Checks if a user should skip the resume upload page
 * Uses /me endpoint with JWT authentication
 * @returns true if user should skip upload, false otherwise
 */
export async function shouldSkipResumeUpload(): Promise<boolean> {
    try {
        const statusResponse = await onboardingApi.getCurrentOnboardingStatus();
        const status: onboardingApi.OnboardingStatus = (statusResponse.payload || statusResponse) as unknown as onboardingApi.OnboardingStatus;

        // Skip upload if resume is already parsed
        return status.resumeParsed === true;
    } catch (error) {
        // If candidate doesn't exist, don't skip
        return false;
    }
}
